import { useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { IconClose, IconUpload } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import { useUploadBackup } from '../contexts/backups';
import { isWithinSizeLimit, isZipFile } from '../utils/validate';

import '../styles/sg.css';

// Subida de un backup .zip de Software Ganadero (admin-sg.md). El POST es síncrono:
// corre las 3 fases del pipeline y responde al terminar (sin WebSocket). Las fases
// del modal se derivan del status de la mutación (idle | uploading | done | error)
// para NO duplicar el estado del servidor en useState (vite/03 [RIGID]); solo el
// estado de UI (archivo, progreso, sg_codfin, drag) vive en useState.

const NORM_LABEL = { animals: 'Animales', bulls: 'Toros', events: 'Eventos' };
const RECON_ORDER = [
    ['linked',     'enlazados'],
    ['queued',     'en cola'],
    ['unmatched',  'sin match'],
    ['ambiguous',  'ambiguos'],
    ['incomplete', 'incompletos'],
];

// Mensaje accionable por código (admin-sg.md). El 403 lo maneja el interceptor
// global (se omite). El 400 sin fields llega como "Bad Request" genérico (quirk
// del cliente), por eso el texto fijo.
function errorMessage(err) {
    if (!err) return 'No se pudo subir el backup.';
    if (err.isTimeout) return 'La carga tardó demasiado. Intentá de nuevo.';
    switch (err.status) {
        case 400: {
            const first = err.fields && Object.values(err.fields)[0];
            return first ? String(first) : 'El archivo no parece un .zip de backup válido.';
        }
        case 413: return 'El archivo supera el límite de 60 MB.';
        case 422: return 'No se pudo leer el .zip (archivo corrupto o ilegible).';
        case 429: return 'Demasiadas subidas, intentá en un minuto.';
        default:  return err.message || 'No se pudo subir el backup.';
    }
}

export default function UploadModal({ onClose }) {
    const { mutateAsync, status, data, error, reset } = useUploadBackup();

    const [file, setFile]           = useState(null);
    const [sgCodfin, setSgCodfin]   = useState('');
    const [progress, setProgress]   = useState(0);
    const [dragActive, setDragActive] = useState(false);
    const inputRef = useRef(null);

    const phase = status === 'pending' ? 'uploading'
        : status === 'success' ? 'done'
        : status === 'error' ? 'error'
        : 'idle';
    const uploading = phase === 'uploading';

    // Valida extensión + tamaño ANTES de enviar; rechaza sin golpear la red.
    const pickFile = (f) => {
        if (!f) return;
        if (!isZipFile(f)) { toast.error('El archivo debe tener extensión .zip'); return; }
        if (!isWithinSizeLimit(f)) { toast.error('El archivo supera el límite de 60 MB'); return; }
        setFile(f);
    };

    const onDrag = (e) => {
        e.preventDefault(); e.stopPropagation();
        if (uploading) return;
        if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
        else if (e.type === 'dragleave') setDragActive(false);
    };
    const onDrop = (e) => {
        e.preventDefault(); e.stopPropagation();
        if (uploading) return;
        setDragActive(false);
        pickFile(e.dataTransfer?.files?.[0]);
    };

    const submit = async () => {
        if (uploading) return;                          // sin doble submit
        if (!file) { toast.error('Elegí un archivo .zip'); return; }
        if (!isZipFile(file)) { toast.error('El archivo debe tener extensión .zip'); return; }
        if (!isWithinSizeLimit(file)) { toast.error('El archivo supera el límite de 60 MB'); return; }
        setProgress(0);
        try {
            await mutateAsync({
                file,
                sgCodfin: sgCodfin.trim() || undefined,
                onProgress: (evt) => { if (evt.total) setProgress(evt.loaded / evt.total); },
            });
        } catch {
            // El status de la mutación pasa a 'error' → lo muestra la fase de error.
            // El 403 ya lo toasteó el interceptor global; no duplicamos.
        }
    };

    const retry = () => { reset(); setProgress(0); };            // conserva el archivo
    const uploadAnother = () => { reset(); setFile(null); setSgCodfin(''); setProgress(0); };
    const close = () => { if (!uploading) onClose(); };
    const cardRef = useModalDialog(close);

    // ---- resumen del pipeline (fase done) ----
    // El discriminante de "éxito vacío" es ingested === 0 (contrato admin-sg.md): un
    // .zip válido sin tablas SG responde 201 con ingested:0. OJO: en la respuesta del
    // POST el batch.table_counts llega {} incluso en un ingest real (el ejemplo del
    // contrato: ingested:120 con table_counts:{}), así que NO sirve para detectar vacío.
    const idempotent = phase === 'done' && data?.created === false;
    const emptyZip = phase === 'done' && data?.created === true && data?.ingested === 0;

    return (
        <div className="sg-upload" role="dialog" aria-modal="true" aria-label="Subir backup de Software Ganadero">
            <div className="sg-upload__overlay" onClick={close} aria-hidden="true" />
            <div className="sg-upload__card" ref={cardRef}>
                <header className="sg-upload__head">
                    <h2 className="sg-upload__title"><IconUpload /> Subir backup de Software Ganadero</h2>
                    <button type="button" className="sg-upload__close" onClick={close} disabled={uploading} aria-label="Cerrar"><IconClose /></button>
                </header>

                <div className="sg-upload__body">
                    {phase === 'idle' && (
                        <>
                            <div
                                className={`sg-upload__dropzone ${dragActive ? 'sg-upload__dropzone--active' : ''}`}
                                onDragEnter={onDrag}
                                onDragOver={onDrag}
                                onDragLeave={onDrag}
                                onDrop={onDrop}
                                onClick={() => inputRef.current?.click()}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
                            >
                                <input
                                    ref={inputRef}
                                    type="file"
                                    className="sg-upload__input"
                                    accept=".zip,application/zip,application/x-zip-compressed"
                                    onChange={(e) => pickFile(e.target.files?.[0])}
                                />
                                {file ? (
                                    <span className="sg-upload__file">{file.name}</span>
                                ) : (
                                    <span className="sg-upload__hint">Arrastrá el <b>.zip</b> aquí o hacé clic para elegirlo</span>
                                )}
                            </div>

                            <label className="sg-upload__field">
                                <span className="sg-upload__label">Finca SG (opcional)</span>
                                <input type="text" maxLength={6} value={sgCodfin} onChange={(e) => setSgCodfin(e.target.value)} placeholder="sg_codfin" />
                            </label>
                        </>
                    )}

                    {phase === 'uploading' && (
                        <div className="sg-upload__progress">
                            <div className="sg-upload__bar"><span className="sg-upload__bar-fill" style={{ width: `${Math.round(progress * 100)}%` }} /></div>
                            <p className="sg-upload__progress-text">
                                {progress >= 1
                                    ? 'Procesando el pipeline (ingesta → normalización → reconciliación)… no cierres esta ventana.'
                                    : `Subiendo… ${Math.round(progress * 100)}%`}
                            </p>
                        </div>
                    )}

                    {phase === 'done' && idempotent && (
                        <div className="sg-upload__result sg-upload__result--info">
                            <p>Este backup ya había sido procesado (mismo contenido). No se volvió a correr el pipeline.</p>
                        </div>
                    )}

                    {phase === 'done' && !idempotent && emptyZip && (
                        <div className="sg-upload__result sg-upload__result--warn">
                            <p>El .zip se subió pero no contenía tablas de Software Ganadero. No se importó ningún registro.</p>
                        </div>
                    )}

                    {phase === 'done' && !idempotent && !emptyZip && (
                        <div className="sg-upload__result">
                            <p className="sg-upload__result-lead">Ingesta completa: <b>{data.ingested}</b> registros.</p>

                            {data.normalized && (
                                <div className="sg-upload__group">
                                    <span className="sg-upload__group-title">Normalización</span>
                                    <ul className="sg-upload__list">
                                        {Object.entries(data.normalized).map(([k, v]) => (
                                            <li key={k}>
                                                {NORM_LABEL[k] || k}: {(v?.created ?? 0)} creados · {(v?.updated ?? 0)} actualizados · {(v?.skipped ?? 0)} omitidos
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {data.reconciled && (
                                <div className="sg-upload__group">
                                    <span className="sg-upload__group-title">Reconciliación</span>
                                    <p className="sg-upload__recon">
                                        {RECON_ORDER.map(([k, label]) => `${data.reconciled[k] ?? 0} ${label}`).join(' · ')}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {phase === 'error' && (
                        <div className="sg-upload__error">{errorMessage(error)}</div>
                    )}
                </div>

                <footer className="sg-upload__foot">
                    {phase === 'idle' && (
                        <>
                            <button type="button" className="sg-upload__btn sg-upload__btn--ghost" onClick={close}>Cancelar</button>
                            <button type="button" className="sg-upload__btn sg-upload__btn--primary" onClick={submit} disabled={!file}>Subir</button>
                        </>
                    )}
                    {phase === 'uploading' && (
                        <span className="sg-upload__running">Procesando… no cierres esta ventana.</span>
                    )}
                    {phase === 'done' && (
                        <>
                            <button type="button" className="sg-upload__btn sg-upload__btn--ghost" onClick={uploadAnother}>Subir otro</button>
                            <button type="button" className="sg-upload__btn sg-upload__btn--primary" onClick={onClose}>Cerrar</button>
                        </>
                    )}
                    {phase === 'error' && (
                        <>
                            <button type="button" className="sg-upload__btn sg-upload__btn--ghost" onClick={onClose}>Cerrar</button>
                            <button type="button" className="sg-upload__btn sg-upload__btn--primary" onClick={retry}>Reintentar</button>
                        </>
                    )}
                </footer>
            </div>
        </div>
    );
}
